import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import type { ShoppingList, ShoppingListItem } from '../types';
import { normalizeItemOrder, projectItemGroups } from '../../../shared/types/itemOrder';

export type ReorderGuidanceReason = 'offline' | 'saving' | 'syncing' | null;

export const getReorderGuidanceKey = (reason: ReorderGuidanceReason): string | null => {
  if (reason === 'offline') return 'shoppingList.reorderOffline';
  if (reason === 'saving') return 'shoppingList.reorderSaving';
  if (reason === 'syncing') return 'shoppingList.reorderSyncing';
  return null;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Sorts shopping lists by createdAt timestamp in descending order (newest first).
 * This ensures consistent sorting behavior across the application.
 *
 * IMPORTANT: This function assumes all lists have numeric createdAt timestamps.
 * For offline-first consistency, these should be client-generated timestamps
 * that are preserved by the backend during sync operations.
 *
 * @param lists - Array of shopping lists to sort
 * @returns New array with lists sorted by creation time (newest first)
 *
 * @see SORTING_AND_TIMESTAMPS.md for detailed explanation of timestamp handling
 */
export const sortListsByCreatedAt = (lists: ShoppingList[]): ShoppingList[] => {
  return [...lists].sort((a, b) => b.createdAt - a.createdAt);
};

/**
 * Sorts shopping list items by createdAt timestamp in descending order (newest first).
 * This ensures consistent sorting behavior across the application.
 *
 * IMPORTANT: This function assumes all items have numeric createdAt timestamps.
 * For offline-first consistency, these should be client-generated timestamps
 * that are preserved by the backend during sync operations.
 *
 * This function handles both temporary IDs (temp-*) and real IDs, ensuring
 * proper sorting regardless of sync status.
 *
 * @param items - Array of shopping list items to sort
 * @returns New array with items sorted by creation time (newest first)
 *
 * @see SORTING_AND_TIMESTAMPS.md for detailed explanation of timestamp handling
 */
export const sortItemsByCreatedAt = (items: ShoppingListItem[]): ShoppingListItem[] => {
  return [...items].sort((a, b) => b.createdAt - a.createdAt);
};

/**
 * Orders items for list-detail rendering.
 *
 * The base app convention is newest-created item first. When grouping is enabled,
 * checked items are partitioned below unchecked items without changing the
 * newest-first ordering inside either group.
 */
export const orderItemsForListDisplay = (
  items: Array<ShoppingListItem | null | undefined>,
  groupDoneItems: boolean,
  itemOrder?: readonly string[],
): ShoppingListItem[] => {
  const validItems = items.filter((item): item is ShoppingListItem => Boolean(item?.id));
  const baseItems = itemOrder === undefined
    ? sortItemsByCreatedAt(validItems)
    : normalizeItemOrder(validItems, itemOrder);
    return projectItemGroups(baseItems, groupDoneItems);
};

export const orderItemsForPrint = (
  items: ShoppingListItem[],
  groupDoneItems: boolean,
  itemOrder: readonly string[] | undefined,
  includeCheckedItems: boolean,
  reverseOrder: boolean,
): ShoppingListItem[] => {
  const baseItems = itemOrder === undefined
    ? [...items].sort((a, b) => {
      const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return timeA - timeB;
    })
    : orderItemsForListDisplay(items, groupDoneItems, itemOrder);
  const groupedItems = itemOrder === undefined
    ? projectItemGroups(baseItems, groupDoneItems)
    : baseItems;
  const printableItems = includeCheckedItems
    ? groupedItems
    : groupedItems.filter((item): boolean => !item.checked);
  return reverseOrder ? [...printableItems].reverse() : printableItems;
};

/**
 * Translates a message that might be a translation key from the backend
 * @param message - The message from backend (could be a translation key or plain text)
 * @param t - The translation function from useTranslation
 * @param interpolationValues - Optional interpolation values for the translation
 * @returns The translated message or the original message if it's not a translation key
 */
export const translateBackendMessage = (
  message: string | undefined,
  t: (key: string, options?: any) => string,
  interpolationValues?: Record<string, any>
): string => {
  if (!message) return '';

  // Check if the message looks like a translation key (contains dots and common patterns)
  if (
    message.includes('.') && (
      message.includes('stripe.') ||
      message.includes('errors.') ||
      message.includes('messages.') ||
      message.includes('referrals.')
    )
  ) {
    try {
      // Try to translate it as a key with interpolation values if provided
      const translated = t(message, interpolationValues);

      // If the translation returns the same key, it means the key doesn't exist
      // In that case, return the original message
      if (translated === message) {
        console.warn(`Translation key not found: ${message}`);
        return message;
      }

      return translated;
    } catch (error) {
      console.error(`Translation error for key ${message}:`, error);
      // If translation fails, return the original message
      return message;
    }
  }

  // If it doesn't look like a translation key, return as is
  return message;
};
